import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, Square, Music, VolumeX, Laugh, Sparkles, SkipForward, X as CloseIcon } from "lucide-react";
import client from "@/lib/api";
import { useLang } from "@/lib/lang";
import { speak, stopSpeaking, isSpeechSupported } from "@/lib/voice";

// Simpler than Suantraí: no sleep timer, no immersive mode. Just joy poems + 1 bg music.
const BG_TRACK = `${process.env.REACT_APP_BACKEND_URL}/api/static/audio/barde_hit.mp3`;

export default function Geantrai() {
  const { lang, setLang } = useLang();
  const [poems, setPoems] = useState([]);
  const [speakingId, setSpeakingId] = useState(null);
  const [musicOn, setMusicOn] = useState(false);
  const audioRef = useRef(null);
  const voiceSupported = isSpeechSupported();
  // Dance mode (immersive fullscreen rotating poem + music + TTS)
  const [danceOpen, setDanceOpen] = useState(false);
  const [dancePoemIdx, setDancePoemIdx] = useState(0);
  const rotateTimerRef = useRef(null);

  useEffect(() => {
    client.get("/geantrai/poems", { params: { lang } })
      .then((r) => setPoems(r.data?.poems || []))
      .catch(() => {});
  }, [lang]);

  useEffect(() => () => stopSpeaking(), []);

  const toggleMusic = () => {
    const el = audioRef.current;
    if (!el) return;
    if (musicOn) {
      el.pause();
      setMusicOn(false);
    } else {
      el.volume = 0.35;
      el.play().then(() => setMusicOn(true)).catch(() => {});
    }
  };

  const togglePoem = (id, text) => {
    if (speakingId === id) {
      stopSpeaking();
      setSpeakingId(null);
      return;
    }
    stopSpeaking();
    setSpeakingId(id);
    speak(text, { lang, onEnd: () => setSpeakingId(null) });
  };

  // ---------- Dance mode (fullscreen random rotating poem) ----------
  const pickRandomIdx = useCallback((excludeIdx) => {
    if (poems.length === 0) return 0;
    if (poems.length === 1) return 0;
    let next = Math.floor(Math.random() * poems.length);
    let safety = 10;
    while (next === excludeIdx && safety-- > 0) {
      next = Math.floor(Math.random() * poems.length);
    }
    return next;
  }, [poems.length]);

  const openDance = () => {
    if (poems.length === 0) return;
    const idx = pickRandomIdx(-1);
    setDancePoemIdx(idx);
    setDanceOpen(true);
    // Autoplay music + TTS
    const el = audioRef.current;
    if (el) {
      el.volume = 0.45;
      el.play().then(() => setMusicOn(true)).catch(() => {});
    }
    stopSpeaking();
    const poem = poems[idx];
    if (poem) {
      setSpeakingId(poem.id);
      speak(poem.text, { lang, onEnd: () => setSpeakingId(null) });
    }
  };

  const nextDancePoem = useCallback(() => {
    setDancePoemIdx((prev) => {
      const next = pickRandomIdx(prev);
      stopSpeaking();
      const poem = poems[next];
      if (poem) {
        setSpeakingId(poem.id);
        speak(poem.text, { lang, onEnd: () => setSpeakingId(null) });
      }
      return next;
    });
  }, [pickRandomIdx, poems, lang]);

  const closeDance = () => {
    setDanceOpen(false);
    stopSpeaking();
    setSpeakingId(null);
    const el = audioRef.current;
    if (el) { el.pause(); }
    setMusicOn(false);
  };

  // Auto-rotate every 35s while in dance mode
  useEffect(() => {
    if (!danceOpen) return;
    rotateTimerRef.current = setInterval(nextDancePoem, 35000);
    return () => clearInterval(rotateTimerRef.current);
  }, [danceOpen, nextDancePoem]);

  // ESC to exit dance mode
  useEffect(() => {
    if (!danceOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeDance(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [danceOpen]);

  return (
    <div data-testid="geantrai-page" className="min-h-screen relative"
         style={{
           background: "radial-gradient(ellipse at top, #3a2504 0%, #1f1403 55%, #0a0601 100%)",
           color: "#fff2c7",
           overflowX: "hidden",
         }}>
      <audio ref={audioRef} src={BG_TRACK} loop preload="none" />

      <header className="relative z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#ffb84a" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(80,50,10,0.4)", border: "1px solid rgba(255,184,74,0.3)" }}>
              <button onClick={() => setLang("fr")} type="button" data-testid="geantrai-lang-fr" className="px-2.5 py-1.5"
                      style={{ background: lang === "fr" ? "#ffb84a" : "transparent", color: lang === "fr" ? "#1a0e02" : "#fff2c7", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button" data-testid="geantrai-lang-en" className="px-2.5 py-1.5"
                      style={{ background: lang === "en" ? "#ffb84a" : "transparent", color: lang === "en" ? "#1a0e02" : "#fff2c7", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <button
              type="button"
              onClick={toggleMusic}
              data-testid="geantrai-music-toggle"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.14em]"
              style={{
                background: musicOn ? "rgba(255,184,74,0.3)" : "rgba(80,50,10,0.4)",
                border: `1px solid ${musicOn ? "#ffb84a" : "rgba(255,184,74,0.3)"}`,
                color: musicOn ? "#ffb84a" : "#fff2c7",
              }}
            >
              {musicOn ? <VolumeX size={13} /> : <Music size={13} />}
              {musicOn
                ? (lang === "fr" ? "Couper" : "Mute")
                : (lang === "fr" ? "Musique joyeuse" : "Joy music")}
            </button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-4 pb-10">
        <div className="overline mb-4" style={{ color: "#ffb84a" }}>
          <span className="kicker-line" />
          Geantraí · {lang === "fr" ? "l'air de la joie · 20 poèmes · gratuit illimité" : "the air of joy · 20 poems · free unlimited"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#fff2c7" }}>
          {lang === "fr" ? (
            <><span className="italic" style={{ color: "#ffb84a" }}>Geantraí</span>, la harpe qui fait rire.</>
          ) : (
            <><span className="italic" style={{ color: "#ffb84a" }}>Geantraí</span>, the harp that makes you laugh.</>
          )}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "#f5d99a" }}>
          {lang === "fr"
            ? "Le Geantraí est l'un des 3 airs sacrés de la harpe du Dagda — celui qui provoque la joie irrépressible. Lis ces poèmes, écoute-les, laisse-les faire danser quelque chose en toi."
            : "The Geantraí is one of the 3 sacred airs of the Dagda's harp — the one that provokes irresistible joy. Read these poems, listen to them, let them dance something awake in you."}
        </p>

        <button
          type="button"
          onClick={openDance}
          disabled={poems.length === 0}
          data-testid="geantrai-dance-open"
          className="mt-6 inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(135deg, #ffb84a, #ff6b4a)",
            color: "#1a0e02",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            boxShadow: "0 20px 50px -20px rgba(255,184,74,0.6)",
            opacity: poems.length === 0 ? 0.4 : 1,
          }}
        >
          <Sparkles size={16} />
          {lang === "fr" ? "💃 Mode danse — poème aléatoire plein écran" : "💃 Dance mode — random fullscreen poem"}
        </button>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid gap-5 sm:grid-cols-2">
          {poems.map((p) => (
            <article key={p.id}
                     data-testid={`geantrai-poem-${p.id}`}
                     className="rounded-2xl p-6 relative overflow-hidden transition-all"
                     style={{
                       background: "linear-gradient(180deg, rgba(60,38,8,0.85), rgba(28,18,4,0.9))",
                       border: "1px solid rgba(255,184,74,0.3)",
                       boxShadow: "0 20px 50px -30px rgba(0,0,0,0.7)",
                     }}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-serif text-xl" style={{ color: "#ffb84a" }}>{p.title}</h3>
                  <p className="text-[11px] font-mono uppercase tracking-[0.2em] mt-1" style={{ color: "#c89a4e" }}>
                    {p.subtitle}
                  </p>
                </div>
                {voiceSupported && (
                  <button
                    type="button"
                    onClick={() => togglePoem(p.id, p.text)}
                    data-testid={`geantrai-listen-${p.id}`}
                    aria-label={speakingId === p.id
                      ? (lang === "fr" ? `Arrêter l'écoute de ${p.title}` : `Stop listening to ${p.title}`)
                      : (lang === "fr" ? `Écouter le poème ${p.title}` : `Listen to poem ${p.title}`)}
                    className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full transition-all"
                    style={{
                      background: speakingId === p.id ? "#ffb84a" : "rgba(255,184,74,0.18)",
                      border: `1px solid ${speakingId === p.id ? "#ffb84a" : "rgba(255,184,74,0.4)"}`,
                      color: speakingId === p.id ? "#1a0e02" : "#ffb84a",
                    }}
                    title={speakingId === p.id ? (lang === "fr" ? "Arrêter" : "Stop") : (lang === "fr" ? "Écouter" : "Listen")}
                  >
                    {speakingId === p.id ? <Square size={14} /> : <Play size={14} />}
                  </button>
                )}
              </div>
              <pre className="font-serif text-[14px] leading-[1.75] whitespace-pre-wrap"
                   style={{ color: "#f5d99a", fontFamily: "'EB Garamond', 'Georgia', serif" }}>
                {p.text}
              </pre>
            </article>
          ))}
        </div>
        {poems.length === 0 && (
          <div className="text-center py-20" style={{ color: "#c89a4e" }}>
            <Laugh size={32} className="mx-auto mb-4 opacity-50" />
            {lang === "fr" ? "La harpe s'accorde…" : "The harp is tuning…"}
          </div>
        )}
      </section>

      {/* Dance mode fullscreen overlay */}
      {danceOpen && poems[dancePoemIdx] && (
        <div
          data-testid="geantrai-dance-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={lang === "fr" ? "Mode danse Geantraí" : "Geantraí dance mode"}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            background:
              "radial-gradient(ellipse at center, rgba(80,45,8,0.95) 0%, rgba(28,18,4,0.98) 55%, rgba(10,6,1,1) 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 24px",
            overflow: "hidden",
            animation: "dance-fade 500ms ease forwards",
          }}
        >
          {/* Floating sparkles decoration */}
          <div aria-hidden="true" style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: `
              radial-gradient(circle at 18% 28%, rgba(255,184,74,0.18) 0%, transparent 35%),
              radial-gradient(circle at 82% 72%, rgba(255,107,74,0.14) 0%, transparent 40%)`,
            animation: "dance-pulse 6s ease-in-out infinite",
          }} />

          <div style={{ position: "relative", maxWidth: 820, textAlign: "center", zIndex: 1 }}>
            <div className="mb-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full"
                 style={{
                   background: "rgba(255,184,74,0.15)",
                   border: "1px solid rgba(255,184,74,0.4)",
                   color: "#ffb84a",
                   fontFamily: "'IBM Plex Mono', monospace",
                   fontSize: 10,
                   letterSpacing: "0.2em",
                   textTransform: "uppercase",
                 }}>
              💃 {lang === "fr" ? "Mode danse" : "Dance mode"} · {dancePoemIdx + 1}/{poems.length}
            </div>
            <h2 className="font-serif leading-[1.1] mb-3"
                style={{ color: "#fff2c7", fontSize: "clamp(32px, 5vw, 56px)" }}>
              {poems[dancePoemIdx].title}
            </h2>
            <p style={{
              color: "#c89a4e",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              marginBottom: 36,
            }}>
              {poems[dancePoemIdx].subtitle}
            </p>
            <pre
              key={poems[dancePoemIdx].id /* re-trigger animation on change */}
              className="whitespace-pre-wrap"
              style={{
                color: "#f8e4b0",
                fontFamily: "'EB Garamond', 'Georgia', serif",
                fontSize: "clamp(17px, 1.6vw, 22px)",
                lineHeight: 1.9,
                animation: "dance-slide 700ms ease forwards",
              }}>
              {poems[dancePoemIdx].text}
            </pre>
          </div>

          {/* Controls */}
          <div style={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 12,
            zIndex: 2,
          }}>
            <button
              type="button"
              onClick={nextDancePoem}
              data-testid="geantrai-dance-next"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full"
              style={{
                background: "linear-gradient(135deg, #ffb84a, #ff6b4a)",
                color: "#1a0e02",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                boxShadow: "0 10px 30px -10px rgba(255,184,74,0.6)",
              }}
            >
              <SkipForward size={14} />
              {lang === "fr" ? "Autre poème" : "Another poem"}
            </button>
            <button
              type="button"
              onClick={closeDance}
              data-testid="geantrai-dance-close"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full"
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "#fff2c7",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              <CloseIcon size={14} />
              {lang === "fr" ? "Sortir" : "Exit"}
            </button>
          </div>

          <style>{`
            @keyframes dance-fade {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes dance-slide {
              from { opacity: 0; transform: translateY(14px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes dance-pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
